import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../WithdrawPage/withdrawal.css";
import "./security.css";
import "./disableAccount.css";

function clearAuthTokensAndNotify() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userId");
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event("agce-auth-changed"));
  window.dispatchEvent(new Event("agce-auth-token-changed"));
}

/** Backend `disableAccount` expects `type: "phone"` for SMS; UI state uses `"mobile"`. */
function toDisableAccountSubmitType(uiVerifyModal) {
  if (uiVerifyModal === "mobile") return "phone";
  return uiVerifyModal;
}

/** Match backend send-otp payload (`phone` for SMS when aligned with disable-account). */
function toDisableAccountSendOtpChannel(uiChannel) {
  return uiChannel === "mobile" ? "phone" : "email";
}

const METHOD_PRIORITY = ["passkey", "totp", "email", "mobile"];

/** First enabled method in `METHOD_PRIORITY` (passkey → authenticator → email → SMS). */
const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

function normalizeSecurityMethods(result) {
  const raw =
    result?.data?.security_methods ||
    result?.data?.data?.security_methods ||
    result?.security_methods ||
    result?.data?.securityMethods ||
    null;
  if (!raw || typeof raw !== "object") return null;
  return {
    email: !!raw.email,
    mobile: !!(raw.mobile ?? raw.phone ?? raw.sms),
    totp: !!raw.totp,
    passkey: !!raw.passkey,
  };
}

function pickPreferredVerificationMethod(methods) {
  if (!methods) return null;
  const hit = METHOD_PRIORITY.find((k) => methods[k]);
  return hit || null;
}

const METHOD_STATUS_LABELS = {
  passkey: "Passkey",
  totp: "Authenticator",
  email: "Email",
  mobile: "Mobile",
};

function ChooseVerificationMethodModal({ methods, onClose, onPick, onSecurityUnavailable }) {
  return (
    <div className="withdraw_confirm_overlay pm-choose-method-overlay-stacked" role="presentation">
      <div className="withdraw_choose_modal pm-choose-method-modal" role="dialog" aria-modal="true" aria-labelledby="da-choose-verify-title">
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="da-choose-verify-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button key={key} type="button" className="withdraw_choose_item pm-choose-method-item" onClick={() => onPick(key)}>
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const IMPACT_INTRO = "Please be aware of the following impacts on your account once it is disabled:";

const IMPACT_ITEMS = [
  "Any pending withdrawal requests will be canceled.",
  "All trading features on your account will be disabled.",
  "All API keys linked to your account will be removed.",
  "Your identity verification details will be retained and not deleted.",
];

/**
 * Disable Account — impacts + priority verification (passkey → TOTP → email → mobile).
 * Route: /user_profile/security/disableAccount
 */
const DisableAccount = () => {
  const navigate = useNavigate();
  const { userDetails } = useContext(ProfileContext);

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");

  const maskedEmail = useMemo(() => maskEmailForDisplay(accountEmail), [accountEmail]);
  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  /** Normalized flags from GET `get-security-methods-list` (loaded on mount). */
  const [resolvedMethods, setResolvedMethods] = useState(null);
  const [methodsPrefetchLoading, setMethodsPrefetchLoading] = useState(true);
  const [methodsPrefetchError, setMethodsPrefetchError] = useState("");
  /** null | "passkey" | "totp" | "email" | "mobile" */
  const [verifyModal, setVerifyModal] = useState(null);
  const [chooseMethodsOpen, setChooseMethodsOpen] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  /** Only after a failed/cancelled passkey attempt — avoids stacking UI while WebAuthn is active. */
  const [passkeyAlternateEligible, setPasskeyAlternateEligible] = useState(false);

  const goSecurity = () => navigate("/user_profile/security", { replace: true });
  const goSecurityUnavailable = () => navigate("/asset_managemnet/security_verification_unavailable");

  const handleDisableSuccess = useCallback(
    (message) => {
      const msg =
        typeof message === "string" && message.trim()
          ? message.trim()
          : "Your account has been disabled.";
      alertSuccessMessage(msg);
      clearAuthTokensAndNotify();
      navigate("/login", { replace: true });
    },
    [navigate]
  );

  const prefetchSecurityMethods = useCallback(async () => {
    setMethodsPrefetchLoading(true);
    setMethodsPrefetchError("");
    try {
      const result = await AuthService.getSecurityMethodsList();
      const m = normalizeSecurityMethods(result);
      if (!result?.success || !m) {
        setResolvedMethods(null);
        setMethodsPrefetchError(result?.message || "Could not load security methods.");
        return;
      }
      setResolvedMethods(m);
    } catch (e) {
      setResolvedMethods(null);
      setMethodsPrefetchError(e?.message || "Network error. Please try again.");
    } finally {
      setMethodsPrefetchLoading(false);
    }
  }, []);

  useEffect(() => {
    void prefetchSecurityMethods();
  }, [prefetchSecurityMethods]);

  const hasVerificationMethod = useMemo(
    () => resolvedMethods != null && pickPreferredVerificationMethod(resolvedMethods) != null,
    [resolvedMethods]
  );

  const resetVerificationInputs = useCallback(() => {
    setOtpCode("");
    setTotpCode("");
  }, []);

  const closeVerifyFlow = useCallback(() => {
    setVerifyModal(null);
    setChooseMethodsOpen(false);
    setPasskeyAlternateEligible(false);
    resetVerificationInputs();
  }, [resetVerificationInputs]);

  /** @returns {Promise<"success"|"cancelled"|"failed"|"invalid">} */
  const verifyPasskey = useCallback(
    async (opts = {}) => {
      const { silentCancel = false } = opts;
      const signId = accountEmail || accountPhoneDigits;
      if (!signId) {
        alertErrorMessage("Account identifier missing for passkey verification.");
        return "invalid";
      }
      setPasskeyAlternateEligible(false);
      setPasskeyBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        const optionsResult = await AuthService.passkeyGetAuthOptions(signId);
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        let credential;
        LoaderHelper.loaderStatus(false);
        try {
          credential = await startAuthentication(optionsResult.data);
        } catch (webauthnError) {
          LoaderHelper.loaderStatus(false);
          if (webauthnError?.name === "NotAllowedError") {
            if (!silentCancel) {
              alertErrorMessage("Authentication was cancelled or timed out. Try again or use another method.");
            }
            setPasskeyAlternateEligible(true);
            return "cancelled";
          }
          alertErrorMessage("Passkey verification failed.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        LoaderHelper.loaderStatus(true);
        const verifyResult = await AuthService.securityDisableAccount({
          type: "passkey",
          code: "",
          credential,
        });
        if (verifyResult?.success) {
          handleDisableSuccess(verifyResult?.message);
          return "success";
        }
        alertErrorMessage(verifyResult?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } catch (e) {
        alertErrorMessage(e?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } finally {
        setPasskeyBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [accountEmail, accountPhoneDigits, handleDisableSuccess]
  );

  const alternateMethodKeys = useMemo(() => {
    if (!resolvedMethods || !verifyModal) return [];
    return METHOD_PRIORITY.filter((k) => resolvedMethods[k] && k !== verifyModal);
  }, [resolvedMethods, verifyModal]);

  const sendDisableOtp = useCallback(async (target) => {
    const security_methods = toDisableAccountSendOtpChannel(target);
    setOtpSending(true);
    try {
      const result = await AuthService.securityDisableAccountSendOtp(security_methods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, []);

  const openChooseMethods = useCallback(() => {
    if (alternateMethodKeys.length === 0) {
      alertErrorMessage("No other verification method is available.");
      return;
    }
    setChooseMethodsOpen(true);
  }, [alternateMethodKeys.length]);

  const applyVerificationMethod = useCallback(
    async (method) => {
      setChooseMethodsOpen(false);
      resetVerificationInputs();
      if (method === "passkey") {
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }
      setVerifyModal(method);
    },
    [resetVerificationInputs, verifyPasskey, closeVerifyFlow]
  );

  const handleDisableAccountClick = async () => {
    if (flowBusy || passkeyBusy || methodsPrefetchLoading) return;
    if (methodsPrefetchError && !resolvedMethods) {
      alertErrorMessage(methodsPrefetchError);
      return;
    }
    if (!hasVerificationMethod) {
      alertErrorMessage(
        "Please add a verification method first"
      );
      return;
    }
    const methodsObj = resolvedMethods;
    const preferred = pickPreferredVerificationMethod(methodsObj);
    if (!preferred) return;

    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      resetVerificationInputs();

      if (preferred === "passkey") {
        setChooseMethodsOpen(false);
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }

      setVerifyModal(preferred);
    } catch (e) {
      alertErrorMessage(e?.message || "Something went wrong.");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyTotpSubmit = async () => {
    const code = String(totpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return;
    }
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.securityDisableAccount({
        type: "totp",
        code,
        credential: "",
      });
      if (res?.success) {
        handleDisableSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Verification failed");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyEmailOrMobileSubmit = async () => {
    const code = String(otpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) return;
    const type = toDisableAccountSubmitType(verifyModal);
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.securityDisableAccount({
        type,
        code,
        credential: "",
      });
      if (res?.success) {
        handleDisableSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Invalid OTP");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePasteTotp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) setTotpCode(digits);
    } catch {
      // ignore
    }
  };

  const showChooser = chooseMethodsOpen && verifyModal;

  return (
    <main className="da-page" aria-labelledby="da-title">
      <nav className="da-page__crumbs" aria-label="Breadcrumb">
        <ol className="da-page__crumbList">
          <li className="da-page__crumbItem">
            <button type="button" className="da-page__crumbLink" onClick={goSecurity}>
              Security
            </button>
          </li>
          <li className="da-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="da-page__crumbItem da-page__crumbItem--active" aria-current="page">
            Disable Account
          </li>
        </ol>
      </nav>

      <h1 id="da-title" className="da-page__title">
        Disable Account
      </h1>

      <div className="da-inner">
        <div className="da-art" aria-hidden="true">
          <img className="dark_img" src="/images/security/disable_vector.svg" alt="" />
          <img className="light_img" src="/images/security/disable_vector_light.svg" alt="" />
        </div>

        <p className="da-intro">{IMPACT_INTRO}</p>

        <ul className="da-list">
          {IMPACT_ITEMS.map((text) => (
            <li key={text} className="da-list__item">
              <span className="da-list__hex" aria-hidden="true" />
              <span className="da-list__text">{text}</span>
            </li>
          ))}
        </ul>

        {methodsPrefetchLoading ? (
          <p className="da-prefetchHint" role="status">
            Checking your security methods…
          </p>
        ) : null}

        {!methodsPrefetchLoading && methodsPrefetchError ? (
          <div className="da-errorBanner da-errorBanner--page" role="alert">
            <p className="da-errorBanner__text">{methodsPrefetchError}</p>
            <button type="button" className="da-btnGhost da-btnGhost--inline" onClick={() => void prefetchSecurityMethods()}>
              Retry
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className="da-btnPrimary"
          disabled={flowBusy || passkeyBusy || methodsPrefetchLoading}
          onClick={() => void handleDisableAccountClick()}
        >
          {flowBusy || passkeyBusy ? "Loading…" : "Disable Account"}
        </button>
      </div>

      {verifyModal === "passkey" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation">
          <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_passkey_title">Verify with passkey</div>
            <div className="withdraw_passkey_hero" aria-hidden="true">
              <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
              <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
            </div>
            <div className="withdraw_passkey_text">Complete verification using your passkey</div>
            <div className="withdraw_passkey_subtext">
              Please follow the instructions on your browser to complete verification.
            </div>
            <button
              type="button"
              className="withdraw_passkey_verify"
              disabled={passkeyBusy}
              onClick={() => void verifyPasskey({ silentCancel: false })}
            >
              {passkeyBusy ? "Verifying…" : "Verify"}
            </button>
            {alternateMethodKeys.length > 0 && passkeyAlternateEligible ? (
              <button type="button" className="withdraw_passkey_link" onClick={openChooseMethods}>
                Use another verification method
              </button>
            ) : null}
            <button type="button" className="withdraw_passkey_link" onClick={goSecurityUnavailable}>
              Security verification unavailable?
            </button>
            <div className="withdraw_passkey_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {verifyModal === "totp" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
          <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_auth_title">Authenticator App Verification</div>
            <div className="withdraw_auth_subtitle">
              Enter the 6-digit code generated by the
              <br />
              Authenticator App.
            </div>
            <div className="withdraw_auth_label">Authenticator App</div>
            <div className="withdraw_auth_input_row">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder=""
                value={totpCode}
                onChange={(e) => setTotpCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="withdraw_auth_paste" onClick={() => void handlePasteTotp()}>
                Paste
              </button>
            </div>
            <button type="button" className="withdraw_auth_submit" disabled={flowBusy} onClick={() => void verifyTotpSubmit()}>
              {flowBusy ? "Verifying…" : "Submit"}
            </button>
            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="withdraw_auth_link" onClick={openChooseMethods}>
                  Switch to Another Verification Method
                </button>
              ) : null}
              <button type="button" className="withdraw_auth_link" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>
            <div className="withdraw_passkey_footer withdraw_auth_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {(verifyModal === "email" || verifyModal === "mobile") && !showChooser ? (
        <div className="tf-sec-page__daev-overlay" role="presentation">
          <div
            className="tf-sec-page__daev-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="da-daev-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="tf-sec-page__daev-close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>

            <h2 id="da-daev-title" className="tf-sec-page__daev-title">
              {verifyModal === "mobile" ? "Mobile Verification" : "Email Verification"}
            </h2>
            <p className="tf-sec-page__daev-desc">
              {verifyModal === "mobile"
                ? `Tap Get Code to receive a 6-digit OTP at ${maskedPhone}, then enter it below.`
                : `Tap Get Code to receive a 6-digit OTP at ${maskedEmail}, then enter it below.`}
            </p>

            <label className="tf-sec-page__daev-label" htmlFor="da-daev-code">
              Enter Verification Code
            </label>
            <div className="tf-sec-page__daev-inputWrap">
              <input
                id="da-daev-code"
                className="tf-sec-page__daev-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder=""
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button
                type="button"
                className="tf-sec-page__daev-getCode"
                disabled={otpSending}
                onClick={() => void sendDisableOtp(verifyModal)}
              >
                {otpSending ? "Sending…" : "Get Code"}
              </button>
            </div>

            <button
              type="button"
              className="tf-sec-page__daev-submit"
              disabled={otpCode.length !== 6 || flowBusy}
              onClick={() => void verifyEmailOrMobileSubmit()}
            >
              {flowBusy ? "Verifying…" : "Submit"}
            </button>

            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="tf-sec-page__daev-help" onClick={openChooseMethods}>
                  Switch verification method
                </button>
              ) : null}
              <button type="button" className="tf-sec-page__daev-help" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>

            <div className="tf-sec-page__daev-footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {showChooser ? (
        <ChooseVerificationMethodModal
          methods={alternateMethodKeys}
          onClose={() => setChooseMethodsOpen(false)}
          onPick={(key) => void applyVerificationMethod(key)}
          onSecurityUnavailable={goSecurityUnavailable}
        />
      ) : null}
    </main>
  );
};

export default DisableAccount;
